import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Globe, Moon, Sun } from "lucide-react";
import { keys } from "@/lib/i18n/portal";
import { LoginForm } from "@/modules/auth/components/LoginForm";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "@admin/app/locale";
import { setThemePref, useTheme } from "@/shared/theme/useTheme";
import { localizedPath } from "@/shared/layout/locale-path";
import { Button, Icon, Illustration, type BlobTone } from "@/shared/rd";

const SLIDES: Array<{ illustration: string; tone: BlobTone; title: string; sub: string }> = [
  { illustration: "admin-welcome", tone: "green", title: keys.shell.slide1Title, sub: keys.shell.slide1Sub },
  { illustration: "admin-billing", tone: "purple", title: keys.shell.slide2Title, sub: keys.shell.slide2Sub },
  { illustration: "admin-alerts", tone: "blue", title: keys.shell.slide3Title, sub: keys.shell.slide3Sub },
];
const SLIDE_MS = 4500;

/**
 * The screen before the shell exists, as the canvas's Login board: the form
 * on the start side; on the end side three illustrated slides about what the
 * admin is for, over a slow morphing blob whose colour follows the slide.
 * `rd-root` because the kit's base styles are scoped to it and no shell
 * carries them here.
 */
export function LoginPage() {
  const t = useTranslations();
  const locale = useLocale();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { resolvedDark } = useTheme();
  const s = keys.shell;
  const [slide, setSlide] = useState(0);
  const [autoplay, setAutoplay] = useState(true);

  useEffect(() => {
    if (!autoplay) return;
    const id = window.setInterval(() => setSlide((current) => (current + 1) % SLIDES.length), SLIDE_MS);
    return () => window.clearInterval(id);
  }, [autoplay]);

  const current = SLIDES[slide];
  const otherLocale = locale === "ar" ? "en" : "ar";

  return (
    <main
      className="rd-root rd-login"
      style={{
        // `svh`, not `vh`: on a phone `100vh` is the URL-bar-retracted height (apps/admin/AGENTS.md).
        minHeight: "100svh",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "stretch",
        position: "relative",
        overflow: "hidden",
        background: "var(--rd-surface)",
        color: "var(--rd-text)",
      }}
    >
      <div className="rd-login-blob" aria-hidden="true" style={{ background: `var(--rd-blob-${current.tone})` }} />

      <div style={{ flex: "1 1 520px", minWidth: 0, display: "flex", flexDirection: "column", position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", height: 64, padding: "0 28px", gap: 10 }}>
          <img src={resolvedDark ? "/trioty-mark-light.svg" : "/trioty-mark-dark.svg"} alt="" width={28} height={28} style={{ borderRadius: 7 }} />
          <span style={{ fontSize: 15, fontWeight: 700 }}>{t(s.brand)}</span>
        </div>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "8px 28px 110px" }}>
          <div style={{ width: "100%", maxWidth: 360, display: "flex", flexDirection: "column" }}>
            <h1 style={{ margin: 0, fontSize: 30, fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.02em" }}>{t(s.loginTitle)}</h1>
            <p style={{ margin: "8px 0 0", fontSize: 15, lineHeight: 1.5, color: "var(--rd-text-secondary)" }}>{t(s.loginSub)}</p>
            <div style={{ marginTop: 28 }}>
              <LoginForm locale={locale} />
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          flex: "1 1 520px",
          minWidth: 0,
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 28,
          padding: "72px 40px 48px",
          boxSizing: "border-box",
          position: "relative",
        }}
      >
        <div style={{ position: "absolute", top: 18, insetInlineEnd: 24, display: "flex", alignItems: "center", gap: 6 }}>
          <button
            type="button"
            className="rd-login-pill"
            onClick={() => navigate(localizedPath(pathname, search, otherLocale), { replace: true })}
          >
            <Icon icon={Globe} size={14} color="var(--rd-text-icon)" />
            <span>{otherLocale === "ar" ? "العربية" : "English"}</span>
          </button>
          <Button
            variant="ghost"
            iconOnly
            icon={resolvedDark ? Sun : Moon}
            title={resolvedDark ? t(s.themeLight) : t(s.themeDark)}
            onClick={() => setThemePref(resolvedDark ? "light" : "dark")}
          />
        </div>
        <div key={current.illustration} className="rd-login-art" style={{ width: 340, maxWidth: "100%", aspectRatio: "1", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Illustration id={current.illustration} size={340} />
        </div>
        <div style={{ maxWidth: 380, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, textAlign: "center" }} aria-live="polite">
          <span style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.25, letterSpacing: "-0.01em", textWrap: "balance" }}>{t(current.title)}</span>
          <span style={{ fontSize: 15, lineHeight: 1.5, color: "var(--rd-text-secondary)", textWrap: "pretty" }}>{t(current.sub)}</span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {SLIDES.map((item, index) => (
            <button
              key={item.illustration}
              type="button"
              aria-label={t(item.title)}
              aria-current={index === slide ? "true" : undefined}
              onClick={() => {
                setAutoplay(false);
                setSlide(index);
              }}
              className="rd-login-dot"
              style={{
                width: index === slide ? 22 : 8,
                background: index === slide ? "var(--rd-text)" : "var(--rd-toggle-off)",
              }}
            />
          ))}
        </div>
      </div>
    </main>
  );
}
